// ============================================================
// V2 app bootstrap — login gate, sidebar shell, route dispatch.
//
// Phase 5A expansion: People section added between Procure and Admin.
//   Main    — Dashboard / Inventory / Suppliers
//   Procure — Requisitions / Purchase Orders / Goods Received / Supplier Invoices
//   People  — Employees (HR/director) · Leave Management (everyone)
//   My Account — My Profile · My Payslips · Generate Payslip (HR/director)
//                (V1 mirrored — every signed-in CCY user sees My Profile.
//                 Avatar click in topbar still navigates to it.)
//   Admin   — Settings
//
// R2 (2026-04-23) — Inventory canary: inventory route now sourced from
// the module manifest (src/modules/inventory/manifest.js) via the platform
// registry; the rest stay hardcoded until R3.
//
// Sidebar is grouped by section. Section visibility filtered by role.
// ============================================================

import './style.css';
import {
  loginWithGoogle, loginWithUsername, changeOwnPassword,
  logout, onAuth, getUserRole, getUserTenant, auth as fbAuth,
  functions as fbFunctions
} from './firebase.js';
import { httpsCallable } from 'firebase/functions';
import { ensureUserDoc, fetchUserDoc, subscribeUserDoc } from './users.js';
// Access-lifecycle (2026-07-15) — "stopped staff" (offboarding) overlay.
// Single source of the stopped predicate + the 3-perm keep-list.
import { isAccessStopped, STOPPED_KEEP_PERMS } from './platform/access-lifecycle.js';
import {
  setCurrentTenant, loadTenantConfig, DEFAULT_TENANT_ID,
  resolveSubdomainFromHostname, loadPublicTenantBranding
} from './platform/tenant.js';
import { registry } from './platform/registry.js';
import { loadRolesForTenant, hasPermission } from './platform/permissions.js';
import { applyTenantTheme, resetTenantTheme } from './platform/theme.js';
import { $, el, showToast } from './ui.js';
// U1 (2026-04-26) — global "Report a problem" topbar button.
// Per DESIGN_LOG U1 sub-decision 2 it lives in the topbar, NOT as a
// bottom-left FAB (mobile-web-expert vetoed bottom-left).
import { openReportModal } from './components/report-modal.js';

// ─────────────────────────────────────────────────────────────
// A3 (2026-04-25) — Resolved subdomain + pre-auth branding cache.
// Set ONCE at boot by `_resolveSubdomainBranding()`. Read by
// `bootstrapLoginDOM()` to render the branded login screen and to
// pre-fill the username form's tenant field.
//   _resolvedSubdomain : string | null   ('acme' or null on fallback URLs)
//   _resolvedBranding  : object | null   (slim branding from public mirror)
// Both stay null on fallback URLs (ccy-erp-v2.web.app, localhost).
// ─────────────────────────────────────────────────────────────
let _resolvedSubdomain = null;
let _resolvedBranding  = null;

// R2 — Inventory canary. The inventory module is loaded via its
// manifest (lazy/dynamic-import inside registry.loadEnabledModules).
// All other modules remain hardcoded imports until R3 retrofits them.
import inventoryManifest from './modules/inventory/manifest.js';
registry.register('inventory', inventoryManifest);

// Phase 6 — Sales canary (manifest from day one). Depends on inventory
// (DO posting calls commitStockMovement). Registry's dependency validator
// enforces inventory loads first; tenantConfig.enabledModules MUST list
// 'inventory' before 'sales' or boot fails fast.
import salesManifest from './modules/sales/manifest.js';
registry.register('sales', salesManifest);

// A2 (2026-04-25) — Invoicing module split out from Sales. Closes the
// R4 Services-pack deferral (services tenants now ship customer master +
// direct invoicing without inventory). See DESIGN_LOG A2 entry.
//
// `supersededBy: ['sales']` in the manifest means: when BOTH sales and
// invoicing are enabled on the same tenant, sales wins the customers +
// customer-invoices routes (sales is the richer module). Invoicing's
// bundle still loads but its routes are dropped from the sidebar by
// registry.getRoutesFor() — see DESIGN_LOG A2 sub-decision 2 for why
// (UX rejects two "Customers" sidebar items).
import invoicingManifest from './modules/invoicing/manifest.js';
registry.register('invoicing', invoicingManifest);

// R3 — Suppliers retrofit. No dependencies. Foundation module for
// procurement + sales (both consume supplier links).
import suppliersManifest from './modules/suppliers/manifest.js';
registry.register('suppliers', suppliersManifest);

// R3 — Procurement retrofit (PR/PO/GRN/SI bundled per design-debate P3).
// Depends on inventory (GRN posts stock movements) + suppliers (PO picker).
import procurementManifest from './modules/procurement/manifest.js';
registry.register('procurement', procurementManifest);

// R3 — HR retrofit. Foundation module; Leave + Payroll depend on it.
import hrManifest from './modules/hr/manifest.js';
registry.register('hr', hrManifest);

// R3 — Leave retrofit. Depends on hr (employee resolution + entitlement seeding).
import leaveManifest from './modules/leave/manifest.js';
registry.register('leave', leaveManifest);

// R3 — Payroll retrofit. Depends on hr (employee + sensitive-field redaction).
import payrollManifest from './modules/payroll/manifest.js';
registry.register('payroll', payrollManifest);

// Attendance (2026-07-27) — QR clock-in/out, 8-hour daily minimum, evidence-
// backed exception approvals. Depends on hr (employee resolution). Payroll
// reads the frozen AttendanceSummary off the payslip doc rather than importing
// this module, so payroll still works for a tenant with attendance disabled.
import attendanceManifest from './modules/attendance/manifest.js';
registry.register('attendance', attendanceManifest);

// R3 — Dashboard retrofit. Manifest-driven widget composition.
// No dependencies — reads from every other module via the registry contract.
import dashboardManifest from './modules/dashboard/manifest.js';
registry.register('dashboard', dashboardManifest);

// R3 — Settings retrofit. Depends on hr + leave (taxonomy + holiday editors).
import settingsManifest from './modules/settings/manifest.js';
registry.register('settings', settingsManifest);

// R3 — Account retrofit. Thin self-service module (My Profile, eventually
// my-settings, my-notifications). Depends on hr + leave + payroll for
// the profile page's data subscriptions.
import accountManifest from './modules/account/manifest.js';
registry.register('account', accountManifest);

// Finance (2026-05-20) — Expenses & Purchases module. Admin tier only
// (director/admin/finance); staff + manager never see it (visible() gates
// on finance.expense.read, which only the admin tier holds). NO dependency
// on inventory (pure-spend v1) so it ships in every pack incl. Services.
import financeManifest from './modules/finance/manifest.js';
registry.register('finance', financeManifest);

// Roadmap & Strategy (2026-08-15) — director + admin ONLY (confidential
// like pricing). ONE sidebar entry in the Admin section (above Settings).
// No dependencies; ships in every industry pack. Spec: SPEC_ROADMAP.md.
import roadmapManifest from './modules/roadmap/manifest.js';
registry.register('roadmap', roadmapManifest);

// Staff Policies (2026-09-21) — numbered, versioned, bilingual EN/BM company
// policy library. READ-ONLY for everyone including shop-floor staff; there is
// ZERO client write path in v1 (publishing is Admin-SDK-only via
// scripts/seed-policies.js). ONE sidebar entry in the People section,
// immediately after Apply Leave (sidebarPosition 30). No dependencies; ships
// in every industry pack. `policies.read` is in STOPPED_KEEP_PERMS — a
// departing worker keeps it.
import policiesManifest from './modules/policies/manifest.js';
registry.register('policies', policiesManifest);

const ALLOWED_DOMAIN     = '@ccyengineering.com';
const TENANT_LOCAL_SUFFIX = '.tenant.local';   // Phase 5C synthetic-email marker

function _isAllowedLoginEmail(email) {
  if (!email) return false;
  return email.endsWith(ALLOWED_DOMAIN) || email.endsWith(TENANT_LOCAL_SUFFIX);
}

// ─────────────────────────────────────────────────────────────
// 2026-05-06 — Recent-tenants helpers (login dropdown).
// DESIGN_LOG 2026-05-06 entry: tenant field on fallback URLs becomes
// a <select> populated from per-browser localStorage of tenants the
// user has SUCCESSFULLY signed into on THIS device. No global
// enumeration (auth-security has rejected that since A3). Subdomain
// routes are unaffected — they already use a hidden field.
//
// Storage key:  'v2_recent_tenants'
// Storage shape: JSON array of valid tenantIds, MRU-first, cap 5.
// Validation:    /^[a-z0-9-]{2,63}$/ on BOTH write and read
//                (defense against XSS-poisoning the storage).
// Write trigger: only after loginWithUsername() resolves (success).
// ─────────────────────────────────────────────────────────────
const _RECENT_TENANTS_KEY = 'v2_recent_tenants';
const _RECENT_TENANTS_CAP = 5;

function _isValidTenantSlug(s) {
  return typeof s === 'string' && /^[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/.test(s);
}

function _readRecentTenants() {
  try {
    const raw = localStorage.getItem(_RECENT_TENANTS_KEY);
    if (!raw) return [];
    const arr = JSON.parse(raw);
    if (!Array.isArray(arr)) return [];
    // De-dupe + validate on read.
    const seen = new Set();
    const out  = [];
    for (const t of arr) {
      const v = String(t || '').trim().toLowerCase();
      if (!_isValidTenantSlug(v)) continue;
      if (seen.has(v)) continue;
      seen.add(v); out.push(v);
      if (out.length >= _RECENT_TENANTS_CAP) break;
    }
    return out;
  } catch (e) {
    // Storage disabled / private mode / corrupt JSON → empty list.
    return [];
  }
}

function _writeRecentTenant(tenantId) {
  const v = String(tenantId || '').trim().toLowerCase();
  if (!_isValidTenantSlug(v)) return;
  try {
    const cur = _readRecentTenants();
    // MRU-first: remove existing instance, prepend new.
    const next = [v, ...cur.filter(t => t !== v)].slice(0, _RECENT_TENANTS_CAP);
    localStorage.setItem(_RECENT_TENANTS_KEY, JSON.stringify(next));
  } catch (e) {
    // Storage quota / private mode — silent; the feature is best-effort.
  }
}

/**
 * Render the tenant field for the login form / forgot-password modal on
 * FALLBACK URLs only (subdomain routes use a hidden field, set elsewhere).
 *
 * If the user has previously signed in to one or more tenants on this
 * browser → render a `<select>` listing them MRU-first, plus an
 * "Other (type tenant ID)..." sentinel that reveals a hidden text input.
 *
 * If the recent-tenants list is empty → render the original free-text
 * input directly (no dropdown of zero items).
 *
 * Caller must wire the "Other..." reveal behavior using
 * `_attachTenantFieldHandlers(rootEl, idPrefix)` after the markup is in
 * the DOM.
 *
 * @param {string} idPrefix    e.g. 'login' → ids 'login-tenant',
 *                             'login-tenant-select', 'login-tenant-other'
 * @param {string} prefillValue value to preselect (defaults to MRU)
 * @returns {string} HTML markup for the tenant field row
 */
function _renderTenantField(idPrefix, prefillValue) {
  const recent  = _readRecentTenants();
  const prefill = String(prefillValue || '').trim().toLowerCase();
  const safePre = _isValidTenantSlug(prefill) ? prefill : '';

  if (recent.length === 0) {
    // First-ever-visit fallback: original free-text input, prefill default.
    return `
      <label class="login-label" for="${idPrefix}-tenant">Tenant</label>
      <input type="text" id="${idPrefix}-tenant" autocomplete="off"
             value="${safePre.replace(/[^a-z0-9-]/gi, '')}"
             placeholder="company short id (e.g. ccy-engineering)" autocapitalize="off"
             spellcheck="false" required />
    `;
  }

  // Build options. Selected = prefill if it's in the list, else MRU (recent[0]).
  const selected = (safePre && recent.includes(safePre)) ? safePre : recent[0];
  const options = recent.map(t => {
    const safe = t.replace(/[^a-z0-9-]/g, '');
    const sel  = (t === selected) ? ' selected' : '';
    return `<option value="${safe}"${sel}>${safe}</option>`;
  }).join('');

  // The "Other..." sentinel value is a string we can never confuse with a
  // valid tenant slug (contains '__' which is forbidden by the slug regex).
  const OTHER_SENTINEL = '__other__';

  return `
    <label class="login-label" for="${idPrefix}-tenant-select">Tenant</label>
    <select id="${idPrefix}-tenant-select" class="login-select" autocomplete="off" required>
      ${options}
      <option value="${OTHER_SENTINEL}">Other (type tenant ID)...</option>
    </select>
    <input type="text" id="${idPrefix}-tenant-other" autocomplete="off"
           placeholder="company short id (e.g. ccy-engineering)" autocapitalize="off"
           spellcheck="false" style="display:none;margin-top:8px;" />
    <input type="hidden" id="${idPrefix}-tenant" value="${selected}">
  `;
}

/**
 * Wire the dropdown change handler so:
 *   - selecting a real tenant copies the value into the hidden #${idPrefix}-tenant input
 *   - selecting "Other..." shows the text input and copies its value live into the hidden field
 * Safe to call when no select exists (free-text fallback) — it simply no-ops.
 */
function _attachTenantFieldHandlers(idPrefix) {
  const sel    = document.getElementById(`${idPrefix}-tenant-select`);
  const other  = document.getElementById(`${idPrefix}-tenant-other`);
  const hidden = document.getElementById(`${idPrefix}-tenant`);
  if (!sel || !other || !hidden) return; // free-text fallback path
  const OTHER_SENTINEL = '__other__';

  const sync = () => {
    if (sel.value === OTHER_SENTINEL) {
      other.style.display = '';
      other.required = true;
      hidden.value = (other.value || '').trim().toLowerCase();
    } else {
      other.style.display = 'none';
      other.required = false;
      hidden.value = sel.value;
    }
  };
  sel.addEventListener('change', sync);
  other.addEventListener('input', () => {
    if (sel.value === OTHER_SENTINEL) {
      hidden.value = (other.value || '').trim().toLowerCase();
    }
  });
  sync();
}

// Route registry — declares mount, unmount, label, icon, section, visibility.
// Visible-everywhere routes use `visible: () => true`; admin-only ones gate by role.
const _routes = {
  // dashboard:  injected at boot from src/modules/dashboard/manifest.js
  //             (manifest-driven widget composition — main.js wraps the
  //              registry-supplied mount fn to inject onJumpToX callbacks).
  // inventory:  injected at boot from src/modules/inventory/manifest.js
  // suppliers:  injected at boot from src/modules/suppliers/manifest.js
  //   All three via registry.loadEnabledModules() — see _bootAfterAuth() below.

  // requisitions / purchase-orders / grn / supplier-invoices: all four
  // injected at boot from src/modules/procurement/manifest.js (R3 P3 bundle).

  // People section — HR + Leave management.
  // employees:        injected at boot from src/modules/hr/manifest.js
  // leave-management: injected at boot from src/modules/leave/manifest.js
  // ─────────────────────────────────────────────────────────────
  // MY ACCOUNT section — self-service for every signed-in user.
  // ux-architect (2026-04-23): V1 had a "MY ACCOUNT" sidebar group
  // visible to every employee. Hiding My Profile behind the topbar
  // avatar (Phase 5A) regressed discoverability for username-login
  // staff (Ruzaidi, Emanuel) who are not muscle-memoried to the
  // Google avatar. Restore the section. Topbar avatar still routes
  // here as a redundant entry-point — no UX harm.
  // Section sits BEFORE Admin so non-admin staff (no Settings) end
  // their sidebar with their own self-service items.
  // ─────────────────────────────────────────────────────────────
  // my-profile:                       injected at boot from src/modules/account/manifest.js
  // my-payslips + generate-payslip:   injected at boot from src/modules/payroll/manifest.js
  // settings:                         injected at boot from src/modules/settings/manifest.js
};

// Section ordering + labels — same order as the sidebar render.
// Phase 6 (2026-04-24): adds 'sell' between Procure and People — sourcing
// → selling → people who do both. Mirror symmetry with Procure.
// `account` sits between People (org-wide HR) and Admin (settings) so each
// user's own self-service items are the last thing they see before any
// admin-only entries (which most users do not see at all).
const SECTIONS = [
  ['main',    'Main'],
  ['procure', 'Procure'],
  ['sell',    'Sell'],
  ['finance', 'Finance'],
  ['people',  'People'],
  ['account', 'My Account'],
  ['admin',   'Admin']
];

let _currentRoute  = null;
let _currentUser   = null;
let _currentRole   = null;
let _currentTenant = null;
let _tenantConfig  = null;

// 2026-05-04 sidebar-leak fix — Materialized roles map for the current
// tenant, keyed by roleId → RoleDoc (see platform/permissions.js). Loaded
// once at boot (after _currentRole is set) so the sidebar's visibility
// check is permission-driven, not role-string-based. Without this, every
// route's `visible: () => true` shows to every signed-in user — that's
// how the staff `ruzaidi` was seeing Inventory, Suppliers, all of
// Procure, all of Sell. Each manifest's visible() now gets (role, hasPerm)
// and consults hasPerm(<module>.read) against the materialized doc.
let _rolesMap = null;

// Access-lifecycle (2026-07-15) — is the signed-in user "stopped"
// (offboarded, past their accessCutoffAt)? Set once at boot in
// _bootAfterAuth from the /users/{uid} doc via isAccessStopped(). When
// true, _hasPerm() collapses to the STOPPED_KEEP_PERMS whitelist —
// overriding even the director fast-path — so a departing user of ANY
// role sees only Dashboard / My Payslips / My Profile. Reset to false on
// logout / onAuth(null). We do NOT flip _currentRole or _rolesMap — the
// stop is an overlay, not a role change (reversal is instant on re-boot
// once accessCutoffAt is cleared by reinstateStaff).
let _isStopped = false;

/**
 * Permission check used by sidebar render + navigate gate.
 * Mirrors Firestore Rules' hasPerm() — director is super-user, '*' wins,
 * literal match wins. Returns false if rolesMap hasn't loaded yet
 * (boot before tenant resolved); pair with role-fast-path for system roles.
 */
function _hasPerm(perm) {
  // Access-lifecycle (2026-07-15): a stopped staff holds ONLY the
  // keep-perms. This sits at the very top so it overrides even the
  // director fast-path below — a departing director/admin is stopped too.
  // Mirrors firestore.rules hasPerm()'s `!isStopped()` gate.
  //
  // 2026-09-21 MASK FIX. access-lifecycle.js:31 has always said "A
  // whitelist, not a mask", but the old body here was
  //     if (_isStopped) return STOPPED_KEEP_PERMS.has(perm);
  // which GRANTS: a stopped user got `dashboard.read` even if their role
  // never held it. That was invisible only because every role in every
  // industry pack holds all three original keep-perms. It stops being
  // invisible the moment a keep-perm is NOT universal — `policies.read`
  // is deliberately withheld from `accountant` (2026-05-29 user
  // directive), so under the old code a stopped accountant would have
  // been GRANTED a policy tab their live self never had, then eaten a
  // permission-denied from hasPermRaw() in the Rules.
  //
  // Correct semantics = INTERSECT: keep-perm AND actually held.
  if (_isStopped) {
    if (!STOPPED_KEEP_PERMS.has(perm)) return false;        // mask
    // Boot ordering: _rolesMap loads after the first render. Returning
    // false here would blank the stopped user's shell for one frame and
    // then pop it back. Keep-perms are the minimal surface anyway, so
    // grant provisionally until the map lands, then intersect.
    if (!_rolesMap) return true;
    return hasPermission(_currentRole, perm, _rolesMap);    // then intersect
  }
  // Fast-path: director is super-user even if rolesMap hasn't loaded yet.
  if (_currentRole === 'director') return true;
  return hasPermission(_currentRole, perm, _rolesMap);
}

function renderLoginScreen() {
  $('#login-screen').style.display = 'flex';
  $('#app').classList.remove('active');
}

function renderApp() {
  $('#login-screen').style.display = 'none';
  $('#app').classList.add('active');

  const sidebar = $('#sidebar');
  sidebar.innerHTML = '';
  // R5 — Brand label + logo from tenant config theme. Logo URL (if any)
  // replaces the default initials-box. Tagline replaces "ERP v2 · Firebase".
  const theme     = _tenantConfig?.theme || {};
  const brandLong = theme.brandName || 'CCY Engineering';
  const tagline   = theme.tagline   || 'ERP v2 · Firebase';
  const logoUrl   = theme.logoUrl   || '';
  // Initials fallback: first 3 chars of brand (UPPER). Trims whitespace.
  const brandShort = brandLong.replace(/\s+/g, '').slice(0, 3).toUpperCase();

  const logoBox = logoUrl
    ? el('div', { class: 'logo-box', style: { background: 'transparent', padding: '0', overflow: 'hidden' } },
        el('img', {
          src: logoUrl, alt: brandLong,
          style: { width: '100%', height: '100%', objectFit: 'contain' }
        })
      )
    : el('div', { class: 'logo-box' }, brandShort);

  sidebar.appendChild(el('div', { class: 'sidebar-logo' },
    logoBox,
    el('div', { class: 'logo-text' },
      brandLong,
      el('span', {}, tagline)
    )
  ));

  // Group routes by section, filter by visibility for current role.
  // 2026-05-04 sidebar-leak fix — pass `_hasPerm` so manifests can gate by
  // permission string (the R6 source of truth) instead of `() => true`.
  // Backward-compat: a manifest using only the first arg still works.
  const byKey = { main: [], procure: [], sell: [], finance: [], people: [], account: [], admin: [] };
  for (const [key, route] of Object.entries(_routes)) {
    if (route.section === '_hidden') continue;
    if (!route.visible(_currentRole, _hasPerm)) continue;
    (byKey[route.section] || byKey.main).push([key, route]);
  }

  // 2026-09-21 — sidebarPosition ordering (ARCHITECTURE.md §2, specified at
  // R2 but unimplemented until now). Before this, order inside a section was
  // Object.entries(_routes) insertion order = module-load order, which was
  // itself resolution-order dependent (see the registry determinism fix).
  //
  // Comparator: numeric sidebarPosition first (missing → 999 = sorts last),
  // then a localeCompare tie-break on the route id. The tie-break is the
  // load-bearing half: a route nobody numbered is still DETERMINISTIC
  // (alphabetical) rather than timing-dependent, so a half-numbered sidebar
  // never regresses to "random". Array.prototype.sort is stable in every
  // engine we target (ES2019+), so equal keys keep insertion order too.
  for (const list of Object.values(byKey)) {
    list.sort((a, b) =>
      ((a[1].sidebarPosition ?? 999) - (b[1].sidebarPosition ?? 999)) ||
      a[0].localeCompare(b[0])
    );
  }

  for (const [sectionKey, label] of SECTIONS) {
    if (!byKey[sectionKey] || byKey[sectionKey].length === 0) continue;
    const navSection = el('div', { class: 'nav-section' },
      el('div', { class: 'nav-label' }, label)
    );
    for (const [key, route] of byKey[sectionKey]) {
      // 2026-04-28 — route.label may be either a string OR a function
      // (role) => string. The function form lets a manifest deliver a
      // role-aware label without splitting one route into two (the leave
      // module uses this so staff see "Apply Leave" while HR/director see
      // "Leave Management" — same destination, different verb). Backward
      // compat: every existing manifest passes a string and is unaffected.
      const labelText = (typeof route.label === 'function')
        ? (route.label(_currentRole) || '')
        : route.label;
      navSection.appendChild(el('div', {
        class: 'nav-item' + (key === _currentRoute ? ' active' : ''),
        'data-route': key,
        onclick: () => navigate(key)
      },
        el('span', { class: 'icon' }, route.icon),
        el('span', {}, labelText)
      ));
    }
    sidebar.appendChild(navSection);
  }

  const topbar = $('#topbar');
  topbar.innerHTML = '';
  // M1 (2026-04-26) — Hamburger button for mobile drawer toggle.
  // CSS hides on desktop (>=768px); shown only on phone widths.
  // Click toggles `body.drawer-open` which animates sidebar in.
  topbar.appendChild(el('button', {
    class: 'hamburger-btn',
    type: 'button',
    'aria-label': 'Toggle navigation menu',
    'aria-expanded': 'false',
    onclick: () => _toggleMobileDrawer()
  },
    el('span', { class: 'hamburger-bars' },
      el('span', {}), el('span', {}), el('span', {})
    )
  ));
  // page-title also resolves function-style labels (see comment above).
  const _curRoute = _routes[_currentRoute] || { label: '' };
  const _curLabel = (typeof _curRoute.label === 'function')
    ? (_curRoute.label(_currentRole) || '')
    : _curRoute.label;
  topbar.appendChild(el('div', { class: 'page-title' },
    _curLabel,
    ' ',
    el('span', { class: 'v2-badge' }, 'V2 DEMO')
  ));
  // Topbar: [Report] · name · role · clickable avatar (My Profile) · sign-out.
  // U1 — Report button lives FIRST so it's leftmost in the user-info group;
  // small ghost-icon style keeps it visually subordinate to the avatar/sign-out
  // but always reachable from any page (DESIGN_LOG U1 sub-decision 2).
  topbar.appendChild(el('div', { class: 'user-info' },
    el('button', {
      class: 'topbar-report-btn',
      type: 'button',
      title: 'Report a problem or suggestion',
      'aria-label': 'Report a problem or suggestion',
      onclick: () => openReportModal({
        user: _currentUser,
        role: _currentRole,
        pageLabel: (_routes[_currentRoute] || { label: '' }).label
      })
    },
      el('span', { class: 'topbar-report-icon' }, '💬'),
      el('span', { class: 'topbar-report-label' }, 'Report')
    ),
    el('span', {},
      _currentUser.displayName || _currentUser.email,
      el('span', { class: 'muted' }, ' · ' + (_currentRole || 'guest'))
    ),
    el('div', {
      class: 'user-avatar user-avatar-clickable',
      title: 'My Profile (' + _currentUser.email + ')',
      onclick: () => navigate('my-profile')
    },
      _currentUser.photoURL
        ? el('img', { src: _currentUser.photoURL, alt: '' })
        : (_currentUser.displayName || _currentUser.email).charAt(0).toUpperCase()
    ),
    el('button', { class: 'logout-btn', onclick: () => doLogout() }, 'Sign out')
  ));
}

/**
 * Navigate to a route.
 *
 * 2026-09-21 — gained an optional second arg. `params` is an opaque plain
 * object handed to the route's mount() as ctx.params, for in-app deep links
 * that need to open a specific record (policies: navigate('policies',
 * { policyCode: 'CCY-HR-P01' })). BACKWARD COMPATIBLE: every existing 1-arg
 * call passes undefined, mounts receive ctx.params === undefined, and modules
 * that ignore it are unaffected.
 *
 * This is NOT a router. `params` lives only for the duration of the mount —
 * it is not in the URL, not in history, and does not survive a reload. V2 has
 * ZERO location.hash / pushState / popstate handling anywhere (verified
 * 2026-09-21). A real hash router is a boot-sequence change (a cold link must
 * survive auth → login redirect → tenant resolution → module load) and is its
 * own ticket — principal-architect + firebase-expert.
 *
 * @param {string} routeKey
 * @param {object} [params]  opaque per-navigation payload → ctx.params
 */
function navigate(routeKey, params) {
  if (!_routes[routeKey]) return;
  // Hidden routes (e.g. my-profile) bypass the sidebar visibility check —
  // they're reachable via direct in-app links (avatar click) but not the menu.
  // 2026-05-04 sidebar-leak fix — same (role, hasPerm) signature as render path.
  // Defense-in-depth: even if a stale link survives in the DOM, navigating to
  // a route the user lacks the perm for is rejected with a toast.
  if (_routes[routeKey].section !== '_hidden' && !_routes[routeKey].visible(_currentRole, _hasPerm)) {
    showToast('You do not have access to this section', 'error');
    return;
  }
  if (_currentRoute && _routes[_currentRoute].unmount) {
    _routes[_currentRoute].unmount();
  }
  _currentRoute = routeKey;
  renderApp();
  // 2026-05-29 — Pass `_hasPerm` into module mount so modules can gate
  // UI affordances (Save/Edit/Delete buttons) by literal permission
  // strings instead of role-string comparisons. Existing modules ignore
  // it (backward-compatible). New gate users (finance read-only for the
  // accountant role) consult it inside the page render.
  // Access-lifecycle (2026-07-15) — pass `isStopped` as an OPTIONAL ctx
  // field (does not change the mount(container,{user,role,hasPerm})
  // signature contract — modules that ignore it are unaffected). The
  // dashboard reads it to render the minimal stopped-dashboard; my-profile
  // reads it to skip the now-server-denied leave subscriptions.
  // 2026-09-21 — `params` (see navigate's jsdoc): optional ctx field for
  // in-app deep links. Undefined for every existing call site.
  _routes[routeKey].mount($('#content'), {
    user: _currentUser, role: _currentRole, hasPerm: _hasPerm, isStopped: _isStopped,
    params: params || null,
    // 2026-09-21 — the GENERIC router handle (same name + shape the dashboard
    // host has had since 2026-07-27) promoted to the standard mount ctx, so a
    // full page can cross-link to another route without DOM-clicking its
    // sidebar item (the fallback my-profile.js:123 had to invent). Policies'
    // "Related links" (Apply Leave / My Payslips) is the first consumer.
    // Additive: modules that ignore it are unaffected.
    onNavigate: (key, p) => navigate(key, p)
  });
  // M1 — auto-close the mobile drawer after navigating; on desktop this
  // is a NOOP (the body class is never set above 767px).
  _closeMobileDrawer();
}

// ─────────────────────────────────────────────────────────────
// M1 (2026-04-26) — Mobile drawer (sidebar slides in from left).
// CSS does the heavy lifting: <768px hides the sidebar off-screen
// and shows the hamburger; toggling `body.drawer-open` slides it in
// and reveals the backdrop.
//
// Backdrop is a single DOM node attached to <body> on first open and
// reused. Tap-backdrop or pressing Escape closes the drawer.
// Auto-closes when navigate() runs (so users don't have to dismiss
// the drawer manually after picking a section).
//
// Focus trap is intentionally light — we restore focus to the
// hamburger button on close so keyboard users can re-open without
// hunting for it. Full focus-trap-inside-drawer would require more
// work and is deferred to M2 (a11y polish pass).
// ─────────────────────────────────────────────────────────────
let _drawerBackdropEl = null;

function _ensureDrawerBackdrop() {
  if (_drawerBackdropEl) return _drawerBackdropEl;
  _drawerBackdropEl = document.createElement('div');
  _drawerBackdropEl.className = 'drawer-backdrop';
  _drawerBackdropEl.addEventListener('click', _closeMobileDrawer);
  document.body.appendChild(_drawerBackdropEl);
  return _drawerBackdropEl;
}

function _toggleMobileDrawer() {
  _ensureDrawerBackdrop();
  const open = document.body.classList.toggle('drawer-open');
  const btn = document.querySelector('.hamburger-btn');
  if (btn) btn.setAttribute('aria-expanded', open ? 'true' : 'false');
}

function _closeMobileDrawer() {
  if (!document.body.classList.contains('drawer-open')) return;
  document.body.classList.remove('drawer-open');
  const btn = document.querySelector('.hamburger-btn');
  if (btn) btn.setAttribute('aria-expanded', 'false');
}

// Esc key closes the drawer (mirrors modal Escape behavior).
document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape' && document.body.classList.contains('drawer-open')) {
    _closeMobileDrawer();
  }
});

// If the viewport grows past the mobile breakpoint (e.g. user rotates
// tablet to landscape, or resizes browser), close the drawer so the
// sidebar reverts to its desktop fixed-position state cleanly.
window.addEventListener('resize', () => {
  if (window.innerWidth >= 768) _closeMobileDrawer();
});

async function doLogin() {
  const btn = $('#btn-google-login');
  btn.disabled = true;
  btn.textContent = 'Signing in…';
  try {
    const user = await loginWithGoogle();
    if (user.email && !user.email.endsWith(ALLOWED_DOMAIN)) {
      await logout();
      throw new Error(
        'Only ' + ALLOWED_DOMAIN + ' accounts are allowed. Contact your director for access.'
      );
    }
  } catch (err) {
    showToast(err.message || 'Sign-in failed', 'error');
    btn.disabled = false;
    btn.innerHTML = googleButtonInner();
  }
}

// ─────────────────────────────────────────────────────────────
// Force-password-change screen (Phase 5C; A4 polish 2026-04-25).
// Shown AFTER successful username-auth sign-in when the user's
// /users/{uid} doc has forcePasswordChange:true (set by admin
// create/reset flows, by the V1 migration import, or — A4 — by
// onboard-tenant.js when generating an initial password).
//
// A4 polish (2026-04-25):
//   - Live password-strength indicator (Too short / Weak / Fair / Strong)
//   - Live mismatch indicator on the confirm field (✓ match, ✗ differs)
//   - Upfront requirement text ("at least 8 characters; mix letters,
//     numbers and a symbol for a strong password")
//   - Smart-disabled Save button (only enabled when both inputs ≥8
//     chars AND match) — no error-round-trip needed for obvious cases
//   - Existing Sign-out escape hatch retained — never trap a user
//
// Hard floor: 8 chars (matches changeOwnPassword in firebase.js).
// We RECOMMEND 12+ via the meter but never reject 8 — aligns with
// NIST SP 800-63B (length matters more than complexity rules).
// auth-security approved this floor in DESIGN_LOG A4 sub-decision 3.
// ─────────────────────────────────────────────────────────────
function renderForcePasswordChangeScreen(user) {
  $('#app').classList.remove('active');
  const screen = $('#login-screen');
  screen.style.display = 'flex';
  screen.innerHTML = `
    <div class="login-card">
      <div class="login-logo">🔒</div>
      <h1>Set your password</h1>
      <div class="subtitle">First sign-in — choose a new password you'll remember.</div>

      <form id="force-pw-form" class="login-form">
        <label class="login-label" for="new-pw">New password</label>
        <input type="password" id="new-pw" autocomplete="new-password"
               placeholder="Minimum 8 characters" required minlength="8" />
        <div class="pw-strength" id="pw-strength" aria-live="polite">
          <div class="pw-strength-bar"><div class="pw-strength-fill" id="pw-strength-fill"></div></div>
          <div class="pw-strength-label" id="pw-strength-label">Enter a password</div>
        </div>
        <div class="pw-hint">
          At least 8 characters. We recommend 12+ with a mix of letters, numbers and a symbol.
        </div>

        <label class="login-label" for="new-pw-confirm">Confirm new password</label>
        <input type="password" id="new-pw-confirm" autocomplete="new-password"
               placeholder="Type the same password again" required minlength="8" />
        <div class="pw-match" id="pw-match" aria-live="polite"></div>

        <button type="submit" class="btn btn-primary login-submit" id="btn-save-pw" disabled>Save &amp; continue</button>
        <button type="button" class="btn btn-secondary login-submit" id="btn-force-pw-logout" style="margin-top:8px">Sign out</button>

        <div class="login-error" id="pw-error" style="display:none"></div>
      </form>

      <div class="login-note">
        Signed in as <strong>${(user.displayName || user.email || '').replace(/[<>]/g, '')}</strong>.
        This screen will not appear again after you set your own password.
      </div>
    </div>
  `;
  setTimeout(() => $('#new-pw')?.focus(), 50);

  $('#btn-force-pw-logout').addEventListener('click', async () => {
    await logout();
  });

  // ── Live UI helpers ───────────────────────────────────────
  // Strength scoring: rule-of-thumb (length × charset diversity), not
  // zxcvbn. For an in-house tool this is enough signal; SaaS-grade
  // meter is a future enhancement (DESIGN_LOG A4 known limitations).
  function _scorePassword(pw) {
    if (!pw) return { tier: 0, label: 'Enter a password', cls: '' };
    if (pw.length < 8)   return { tier: 0, label: 'Too short', cls: 'pw-tier-0' };
    let charsets = 0;
    if (/[a-z]/.test(pw)) charsets++;
    if (/[A-Z]/.test(pw)) charsets++;
    if (/[0-9]/.test(pw)) charsets++;
    if (/[^A-Za-z0-9]/.test(pw)) charsets++;
    // Tier from combined length + charsets diversity.
    const score = pw.length + charsets * 2;
    if (score < 12) return { tier: 1, label: 'Weak — try a longer or more varied password', cls: 'pw-tier-1' };
    if (score < 18) return { tier: 2, label: 'Fair — usable, but a longer password is safer', cls: 'pw-tier-2' };
    return { tier: 3, label: 'Strong', cls: 'pw-tier-3' };
  }

  function _updateUI() {
    const a = $('#new-pw').value || '';
    const b = $('#new-pw-confirm').value || '';
    const score = _scorePassword(a);
    const fill  = $('#pw-strength-fill');
    const label = $('#pw-strength-label');
    fill.className = 'pw-strength-fill ' + (score.cls || '');
    label.textContent = score.label;

    const match = $('#pw-match');
    if (!b) {
      match.textContent = '';
      match.className   = 'pw-match';
    } else if (a === b) {
      match.textContent = '✓ Passwords match';
      match.className   = 'pw-match pw-match-ok';
    } else {
      match.textContent = '✗ Passwords do not match';
      match.className   = 'pw-match pw-match-bad';
    }

    // Smart-disable: both inputs ≥8 chars AND match.
    const ok = a.length >= 8 && a === b;
    $('#btn-save-pw').disabled = !ok;
  }

  $('#new-pw').addEventListener('input', _updateUI);
  $('#new-pw-confirm').addEventListener('input', _updateUI);
  _updateUI();   // initial paint

  $('#force-pw-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const errBox = $('#pw-error');
    const btn    = $('#btn-save-pw');
    errBox.style.display = 'none';
    errBox.textContent   = '';
    const a = $('#new-pw').value || '';
    const b = $('#new-pw-confirm').value || '';
    // Defensive double-check (button is smart-disabled but submit can
    // still fire via Enter key in some browsers if focus is unusual).
    if (a.length < 8)  { errBox.textContent = 'Password must be at least 8 characters'; errBox.style.display = ''; return; }
    if (a !== b)       { errBox.textContent = 'Passwords do not match'; errBox.style.display = ''; return; }
    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      await changeOwnPassword(a);
      showToast('Password updated — welcome!', 'success');
      // Trigger the onAuth path: re-read user doc, boot app.
      await _bootAfterAuth(fbAuth.currentUser, { skipForceCheck: true });
    } catch (err) {
      errBox.textContent = err.message || 'Could not save the new password';
      errBox.style.display = '';
      btn.disabled = false;
      btn.textContent = 'Save & continue';
    }
  });
}

async function doLogout() {
  await logout();
  _currentUser = null;
  _currentRole = null;
  _currentRoute = null;
  _rolesMap     = null;   // 2026-05-04 sidebar-leak fix — clear stale perms on logout
  _isStopped    = false;  // access-lifecycle — clear stopped overlay on logout
}

function googleButtonInner() {
  return `
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/>
      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.37-.35-2.09s.13-1.43.35-2.09V7.07H2.18a10.98 10.98 0 0 0 0 9.86l3.66-2.84z"/>
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/>
    </svg>
    <span>Sign in with Google</span>
  `;
}

/**
 * A3 (2026-04-25) — Render the branded login DOM.
 *
 * If the page was opened on a tenant subdomain (e.g. acme.erp.ccyengineering.com),
 * the resolved branding (logo, brand name, tagline, primary color) is applied
 * to the login card and the tenant input row is HIDDEN (subdomain dictates it).
 *
 * On the fallback URL (ccy-erp-v2.web.app or localhost), generic CCY branding
 * is shown and the tenant input row is visible.
 *
 * Pre-auth flow (called by bootstrap at module top):
 *   1. _resolveSubdomainBranding()  — async, sets _resolvedSubdomain + _resolvedBranding
 *   2. bootstrapLoginDOM()          — renders using whatever was resolved
 *
 * If the URL has `?reset=<token>` instead, _maybeRenderResetScreen() takes over
 * (called by the bootstrap before bootstrapLoginDOM).
 */
function bootstrapLoginDOM() {
  const screen = $('#login-screen');

  // Subdomain-driven branding (slim, pre-auth). Falls back to CCY defaults.
  const brand    = _resolvedBranding || {};
  const brandName= brand.brandName  || brand.name || 'CCY ERP';
  const tagline  = brand.tagline    || 'V2 · Firebase Edition';
  const logoUrl  = brand.logoUrl    || '';
  const primary  = brand.primaryColor || '';
  const accent   = brand.accentColor  || '';

  // Apply branded color CSS variables to the login screen so buttons/links
  // pick up the tenant color. Resets to CCY defaults on logout via
  // resetTenantTheme().
  if (primary) {
    document.documentElement.style.setProperty('--accent', primary);
    if (accent) document.documentElement.style.setProperty('--accent2', accent);
  }
  // Document title gets branded too.
  document.title = brandName + ' · ERP';

  const isSubdomainRoute = !!_resolvedSubdomain;
  const tenantHint = _resolvedBranding?.tenantId || '';

  const initials = String(brandName).replace(/\s+/g, '').slice(0, 3).toUpperCase() || 'ERP';
  const logoMarkup = logoUrl
    ? `<img src="${logoUrl}" alt="${brandName.replace(/[<>"]/g, '')}" style="max-width:100%;max-height:100%;object-fit:contain;">`
    : initials;

  // Show a small "tenant info chip" under the brand on subdomain routes —
  // confirms to the user "yes, you're on the ACME instance."
  const tenantChip = isSubdomainRoute
    ? `<div class="login-tenant-chip" title="Subdomain: ${_resolvedSubdomain}.erp.ccyengineering.com">
         Signing in to <strong>${brandName.replace(/[<>"]/g, '')}</strong>
       </div>`
    : '';

  // Two-tab login: Google (default) + Username.
  // On subdomain routes, default tab is Username (most tenant directors
  // will use username auth; the Google tab is the @ccyengineering.com
  // staff-only path).
  const defaultTab = isSubdomainRoute ? 'username' : 'google';
  const tabClass   = (t) => 'login-tab' + (t === defaultTab ? ' active' : '');
  const paneStyle  = (t) => (t === defaultTab ? '' : 'display:none');

  // Hide the tenant input row entirely on subdomain routes. The form
  // still HAS a hidden field — submit code reads from it transparently.
  // On fallback URLs the row is rendered by _renderTenantField, which
  // shows a <select> of recently-used tenants from localStorage (with
  // an "Other..." escape hatch) when the list is non-empty, or the
  // original free-text input when empty. See DESIGN_LOG 2026-05-06.
  const tenantInputRow = isSubdomainRoute
    ? `<input type="hidden" id="login-tenant" value="${tenantHint.replace(/[^a-z0-9-]/gi, '')}">`
    : _renderTenantField('login', DEFAULT_TENANT_ID);

  screen.innerHTML = `
    <div class="login-card">
      <div class="login-logo">${logoMarkup}</div>
      <h1>Sign in</h1>
      <div class="subtitle">${tagline.replace(/[<>]/g, '')} <span class="v2-badge">DEMO</span></div>
      ${tenantChip}

      <div class="login-tabs">
        <button class="${tabClass('google')}" data-tab="google">Sign in with Google</button>
        <button class="${tabClass('username')}" data-tab="username">Sign in with Username</button>
      </div>

      <div class="login-pane" data-pane="google" style="${paneStyle('google')}">
        <button class="btn-google" id="btn-google-login">${googleButtonInner()}</button>
        <div class="login-note">
          Use your <strong>@ccyengineering.com</strong> Google account. No password needed —
          Google handles the sign-in.
          ${isSubdomainRoute ? '<br><br>This sign-in option is for CCY Engineering staff.' : ''}
        </div>
      </div>

      <div class="login-pane" data-pane="username" style="${paneStyle('username')}">
        <form id="username-login-form" class="login-form">
          ${tenantInputRow}

          <label class="login-label">Username</label>
          <input type="text" id="login-username" autocomplete="username"
                 placeholder="${isSubdomainRoute ? 'your username' : 'e.g. ruzaidi'}" autocapitalize="off"
                 spellcheck="false" required />

          <label class="login-label">Password</label>
          <input type="password" id="login-password" autocomplete="current-password"
                 placeholder="Your password" required />

          <button type="submit" class="btn btn-primary login-submit" id="btn-username-login">Sign in</button>

          <div class="login-forgot-row">
            <a href="#" id="link-forgot-password" class="login-forgot-link">Forgot password?</a>
          </div>

          <div class="login-error" id="username-login-error" style="display:none"></div>
        </form>
        <div class="login-note">
          Use the <strong>username</strong> your administrator gave you.
          ${isSubdomainRoute
            ? ''
            : '<br />Tenant + username are usually pre-filled from your sign-in link.'}
        </div>
      </div>
    </div>
  `;

  $('#btn-google-login').addEventListener('click', doLogin);

  // Wire the tenant-dropdown handlers (no-op on subdomain routes — hidden
  // field — and on the empty-recents free-text fallback path).
  if (!isSubdomainRoute) _attachTenantFieldHandlers('login');

  // ── URL-param prefill for sign-in deep links ─────────────────
  // Subdomain routes IGNORE the ?tenant= param (subdomain is authoritative)
  // but still honor ?user= (username prefill) and ?tab=.
  try {
    const params = new URLSearchParams(window.location.search);
    const urlTenant = (params.get('tenant') || '').trim().toLowerCase();
    const urlUser   = (params.get('user')   || '').trim().toLowerCase();
    const urlTab    = (params.get('tab')    || '').trim().toLowerCase();

    if (!isSubdomainRoute && urlTenant) {
      // Three possible field shapes — drive whichever exists:
      //   1. <select> + hidden + "other" text  → select if option exists, else flip to Other
      //   2. <input type=text id=login-tenant>  → just set value
      const sel    = $('#login-tenant-select');
      const other  = $('#login-tenant-other');
      const hidden = $('#login-tenant');
      if (sel && hidden) {
        const match = Array.from(sel.options).find(o => o.value === urlTenant);
        if (match) {
          sel.value = urlTenant;
          hidden.value = urlTenant;
          if (other) { other.style.display = 'none'; other.required = false; }
        } else {
          sel.value = '__other__';
          if (other) { other.style.display = ''; other.value = urlTenant; other.required = true; }
          hidden.value = urlTenant;
        }
      } else if (hidden && hidden.tagName === 'INPUT') {
        hidden.value = urlTenant;
      }
    }
    if (urlUser)             $('#login-username').value = urlUser;
    if (urlTab === 'username') {
      const t = screen.querySelector('.login-tab[data-tab="username"]');
      if (t && !t.classList.contains('active')) t.click();
    }
  } catch (e) { /* best-effort prefill */ }

  // Tab switcher
  screen.querySelectorAll('.login-tab').forEach(t => t.addEventListener('click', () => {
    const tab = t.dataset.tab;
    screen.querySelectorAll('.login-tab').forEach(x => x.classList.toggle('active', x === t));
    screen.querySelectorAll('.login-pane').forEach(p => {
      p.style.display = (p.dataset.pane === tab) ? '' : 'none';
    });
    if (tab === 'username') setTimeout(() => $('#login-password')?.focus() || $('#login-username')?.focus(), 50);
  }));

  // Forgot password link
  $('#link-forgot-password')?.addEventListener('click', (e) => {
    e.preventDefault();
    _openForgotPasswordModal();
  });

  // Username login submit
  $('#username-login-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const errBox = $('#username-login-error');
    const btn    = $('#btn-username-login');
    errBox.style.display = 'none';
    errBox.textContent   = '';
    const u = ($('#login-username').value || '').trim().toLowerCase();
    const p =  $('#login-password').value || '';
    const t = ($('#login-tenant').value   || '').trim().toLowerCase() || DEFAULT_TENANT_ID;
    if (!u || !p) { errBox.textContent = 'Username and password required'; errBox.style.display = ''; return; }
    btn.disabled = true;
    btn.textContent = 'Signing in…';
    try {
      await loginWithUsername(u, p, t);
      // 2026-05-06 — persist the tenant for the recently-used dropdown
      // ONLY on success (failed attempts must not poison the list).
      _writeRecentTenant(t);
      // onAuth handler will take it from here.
    } catch (err) {
      const msg = (err && err.code === 'auth/invalid-credential')
        ? 'Wrong username or password.'
        : (err && err.code === 'auth/user-disabled')
            ? 'This account has been disabled. Contact admin.'
            : (err.message || 'Sign-in failed');
      errBox.textContent = msg;
      errBox.style.display = '';
      btn.disabled = false;
      btn.textContent = 'Sign in';
    }
  });
}

// ─────────────────────────────────────────────────────────────
// A5 (2026-04-25) — Forgot-password modal
// Asks for username + tenant, fires requestPasswordReset Cloud Fn.
// Always shows the SAME success message (no enumeration). The Cloud
// Function itself returns identical responses for found/not-found
// per DESIGN_LOG A5 sub-decision 3.
// ─────────────────────────────────────────────────────────────
function _openForgotPasswordModal() {
  // Remove any prior modal instance (defensive — link can be re-clicked).
  const existing = document.getElementById('forgot-pw-modal');
  if (existing) existing.remove();

  const tenantPrefill = ($('#login-tenant')?.value || _resolvedBranding?.tenantId || DEFAULT_TENANT_ID).trim();
  const userPrefill   = ($('#login-username')?.value || '').trim();
  const isSubdomainRoute = !!_resolvedSubdomain;

  const modal = document.createElement('div');
  modal.id = 'forgot-pw-modal';
  modal.className = 'forgot-pw-modal-backdrop';
  modal.innerHTML = `
    <div class="forgot-pw-modal-card">
      <div class="forgot-pw-modal-header">
        <h2>Reset your password</h2>
        <button class="forgot-pw-close" id="btn-forgot-close" aria-label="Close">×</button>
      </div>
      <div class="forgot-pw-modal-body">
        <p class="forgot-pw-intro">
          Enter your username${isSubdomainRoute ? '' : ' and tenant'}.
          We'll email a password-reset link to the contact email
          on file for your account.
        </p>
        <form id="forgot-pw-form">
          ${isSubdomainRoute
            ? `<input type="hidden" id="forgot-tenant" value="${(tenantPrefill || '').replace(/[^a-z0-9-]/gi, '')}">`
            : _renderTenantField('forgot', tenantPrefill)}
          <label class="login-label">Username</label>
          <input type="text" id="forgot-username" autocomplete="username"
                 value="${userPrefill.replace(/[^a-z0-9_.-]/gi, '')}"
                 autocapitalize="off" spellcheck="false" required />

          <button type="submit" class="btn btn-primary forgot-pw-submit" id="btn-forgot-submit">
            Send reset link
          </button>

          <div class="forgot-pw-result" id="forgot-pw-result" style="display:none"></div>
        </form>
      </div>
    </div>
  `;
  document.body.appendChild(modal);
  setTimeout(() => $('#forgot-username')?.focus(), 50);

  // Wire the tenant-dropdown handlers for the modal (no-op on subdomain
  // routes — hidden field — and on the empty-recents free-text fallback).
  if (!isSubdomainRoute) _attachTenantFieldHandlers('forgot');

  $('#btn-forgot-close').addEventListener('click', () => modal.remove());
  modal.addEventListener('click', (e) => { if (e.target === modal) modal.remove(); });

  $('#forgot-pw-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const btn   = $('#btn-forgot-submit');
    const out   = $('#forgot-pw-result');
    const u     = ($('#forgot-username').value || '').trim().toLowerCase();
    const t     = ($('#forgot-tenant').value   || '').trim().toLowerCase();
    if (!u || !t) {
      out.textContent = 'Username and tenant are required.';
      out.className   = 'forgot-pw-result forgot-pw-result-bad';
      out.style.display = '';
      return;
    }
    btn.disabled = true;
    btn.textContent = 'Sending…';
    out.style.display = 'none';
    try {
      const callable = httpsCallable(fbFunctions, 'requestPasswordReset');
      const resp = await callable({ username: u, tenantId: t });
      const msg = resp?.data?.message || 'If an account exists with that username, a reset link has been sent.';
      out.textContent = msg;
      out.className   = 'forgot-pw-result forgot-pw-result-ok';
      out.style.display = '';
      btn.textContent = 'Sent';
      // Auto-close after 3.5s so the user can re-attempt sign-in or read the message.
      setTimeout(() => { try { modal.remove(); } catch (e) {} }, 3500);
    } catch (err) {
      out.textContent = 'Could not send reset request: ' + (err.message || 'unknown error');
      out.className   = 'forgot-pw-result forgot-pw-result-bad';
      out.style.display = '';
      btn.disabled = false;
      btn.textContent = 'Send reset link';
    }
  });
}

// ─────────────────────────────────────────────────────────────
// A5 (2026-04-25) — Reset-password landing screen
// Triggered by ?reset=<token> in the URL. Validates the token format
// client-side (length + hex), then renders a "set new password" form.
// Calls completePasswordReset Cloud Fn which actually validates the
// token server-side.
// ─────────────────────────────────────────────────────────────
function _maybeRenderResetScreen() {
  let token = '';
  try {
    const params = new URLSearchParams(window.location.search);
    token = String(params.get('reset') || '').trim();
  } catch (e) { return false; }
  if (!token) return false;

  // Client-side format check — defends against accidental garbled URLs.
  // (Server still checks server-side, but a quick local check spares a
  // round-trip for the obvious case.)
  if (!/^[a-f0-9]{64}$/.test(token)) {
    // Don't render the reset form for an obviously broken token —
    // fall through to the normal login screen with an error toast.
    setTimeout(() => showToast(
      'The reset link looks invalid. Please request a new one.', 'error'
    ), 100);
    return false;
  }

  const screen = $('#login-screen');
  const brand    = _resolvedBranding || {};
  const brandName= brand.brandName || brand.name || 'CCY ERP';

  screen.style.display = 'flex';
  $('#app').classList.remove('active');

  screen.innerHTML = `
    <div class="login-card">
      <div class="login-logo">🔑</div>
      <h1>Set a new password</h1>
      <div class="subtitle">${String(brandName).replace(/[<>]/g, '')}</div>

      <form id="reset-pw-form" class="login-form">
        <label class="login-label" for="reset-new-pw">New password</label>
        <input type="password" id="reset-new-pw" autocomplete="new-password"
               placeholder="Minimum 8 characters" required minlength="8" />
        <div class="pw-strength" aria-live="polite">
          <div class="pw-strength-bar"><div class="pw-strength-fill" id="reset-pw-fill"></div></div>
          <div class="pw-strength-label" id="reset-pw-label">Enter a password</div>
        </div>
        <div class="pw-hint">
          At least 8 characters. We recommend 12+ with a mix of letters, numbers and a symbol.
        </div>

        <label class="login-label" for="reset-pw-confirm">Confirm new password</label>
        <input type="password" id="reset-pw-confirm" autocomplete="new-password"
               placeholder="Type the same password again" required minlength="8" />
        <div class="pw-match" id="reset-pw-match" aria-live="polite"></div>

        <button type="submit" class="btn btn-primary login-submit" id="btn-reset-save" disabled>
          Set password &amp; sign in
        </button>

        <div class="login-error" id="reset-pw-error" style="display:none"></div>
      </form>

      <div class="login-note">
        After your password is set you'll be returned to the sign-in screen.
        Reset links expire 24 hours after they're requested.
      </div>
    </div>
  `;
  setTimeout(() => $('#reset-new-pw')?.focus(), 50);

  // Reuse the same _scorePassword helper inline (small enough to duplicate
  // rather than refactor today).
  function _scorePassword(pw) {
    if (!pw) return { tier: 0, label: 'Enter a password', cls: '' };
    if (pw.length < 8)   return { tier: 0, label: 'Too short', cls: 'pw-tier-0' };
    let charsets = 0;
    if (/[a-z]/.test(pw)) charsets++;
    if (/[A-Z]/.test(pw)) charsets++;
    if (/[0-9]/.test(pw)) charsets++;
    if (/[^A-Za-z0-9]/.test(pw)) charsets++;
    const score = pw.length + charsets * 2;
    if (score < 12) return { tier: 1, label: 'Weak — try a longer or more varied password', cls: 'pw-tier-1' };
    if (score < 18) return { tier: 2, label: 'Fair — usable, but a longer password is safer', cls: 'pw-tier-2' };
    return { tier: 3, label: 'Strong', cls: 'pw-tier-3' };
  }

  function _updateUI() {
    const a = $('#reset-new-pw').value || '';
    const b = $('#reset-pw-confirm').value || '';
    const score = _scorePassword(a);
    const fill  = $('#reset-pw-fill');
    const label = $('#reset-pw-label');
    fill.className = 'pw-strength-fill ' + (score.cls || '');
    label.textContent = score.label;

    const match = $('#reset-pw-match');
    if (!b) {
      match.textContent = '';
      match.className   = 'pw-match';
    } else if (a === b) {
      match.textContent = '✓ Passwords match';
      match.className   = 'pw-match pw-match-ok';
    } else {
      match.textContent = '✗ Passwords do not match';
      match.className   = 'pw-match pw-match-bad';
    }

    const ok = a.length >= 8 && a === b;
    $('#btn-reset-save').disabled = !ok;
  }

  $('#reset-new-pw').addEventListener('input', _updateUI);
  $('#reset-pw-confirm').addEventListener('input', _updateUI);
  _updateUI();

  $('#reset-pw-form').addEventListener('submit', async (e) => {
    e.preventDefault();
    const errBox = $('#reset-pw-error');
    const btn    = $('#btn-reset-save');
    errBox.style.display = 'none';
    errBox.textContent   = '';
    const a = $('#reset-new-pw').value || '';
    const b = $('#reset-pw-confirm').value || '';
    if (a.length < 8 || a !== b) {
      errBox.textContent = a.length < 8 ? 'Password must be at least 8 characters' : 'Passwords do not match';
      errBox.style.display = '';
      return;
    }
    btn.disabled = true;
    btn.textContent = 'Saving…';
    try {
      const callable = httpsCallable(fbFunctions, 'completePasswordReset');
      const resp = await callable({ token, newPassword: a });
      const msg = resp?.data?.message || 'Password updated. Sign in with your new password.';
      showToast(msg, 'success');
      // Strip the ?reset= param from the URL so the user lands cleanly on
      // the login screen with no token in history. Keep ?tenant= / ?user=
      // if they were there too.
      try {
        const url = new URL(window.location.href);
        url.searchParams.delete('reset');
        window.history.replaceState(null, '', url.toString());
      } catch (e) {}
      // Re-render the login screen.
      bootstrapLoginDOM();
    } catch (err) {
      const code = err?.code || '';
      const msg = (code === 'functions/failed-precondition')
        ? (err.message || 'This reset link is no longer valid.')
        : (code === 'functions/not-found')
          ? 'This reset link is invalid or has already been used.'
          : (err.message || 'Could not update password.');
      errBox.textContent = msg;
      errBox.style.display = '';
      btn.disabled = false;
      btn.textContent = 'Set password & sign in';
    }
  });

  return true;
}

/**
 * A3 (2026-04-25) — Pre-auth subdomain branding resolution.
 * Runs ONCE at module top before any UI render. On fallback URLs
 * (no subdomain), short-circuits to null so generic CCY login renders.
 *
 * Latency budget per DESIGN_LOG A3 sub-decision 4: ~80-120ms cold,
 * ~30ms warm. Inline spinner already in index.html covers this gap.
 */
async function _resolveSubdomainBranding() {
  try {
    const sub = resolveSubdomainFromHostname(window.location.hostname);
    if (!sub) {
      _resolvedSubdomain = null;
      _resolvedBranding  = null;
      return;
    }
    _resolvedSubdomain = sub;
    const branding = await loadPublicTenantBranding(sub);
    _resolvedBranding = branding;
    if (!branding) {
      console.warn(`[A3] No public-tenant-branding doc for subdomain '${sub}'. Falling back to generic login.`);
      // Subdomain resolved but tenant not found — keep _resolvedSubdomain
      // null so the form shows the tenant input row (gives the user a
      // chance to recover by typing their tenant manually).
      _resolvedSubdomain = null;
    }
  } catch (e) {
    console.warn('[A3] subdomain resolution failed:', e);
    _resolvedSubdomain = null;
    _resolvedBranding  = null;
  }
}

// ─────────────────────────────────────────────────────────────
// Bootstrap entry point — order matters:
//   1. Resolve subdomain branding (async, ~80-120ms)
//   2. If URL has ?reset=<token>, render the reset screen instead
//   3. Otherwise render the (branded) login screen
//
// onAuth handler wires up post-sign-in flow regardless of which path
// rendered first.
// ─────────────────────────────────────────────────────────────
(async () => {
  await _resolveSubdomainBranding();
  if (_maybeRenderResetScreen()) return;
  bootstrapLoginDOM();
})();

async function _bootAfterAuth(user, { skipForceCheck = false } = {}) {
  _currentUser = user;

  // R1 boot order:
  //   1. Resolve tenant FIRST — every subsequent Firestore call needs context.
  //   2. setCurrentTenant() — activates the platform/tenant.js helpers.
  //   3. ensureUserDoc (Google users only) — writes /users/{uid}.
  //      Username-auth users already have a doc created by createStaffLogin.
  //   4. loadTenantConfig — fetches /tenants/{tid} for theme + enabled modules.
  //   5. getUserRole — uses claim/doc fallback as before.
  //   6. Phase 5C: check forcePasswordChange for username users → force screen.
  //   7. navigate('dashboard') — UI renders with tenant brand applied.
  try {
    _currentTenant = await getUserTenant(user);
    setCurrentTenant(_currentTenant);
  } catch (err) {
    console.error('[boot] tenant resolution failed:', err);
    showToast('Could not resolve tenant context. Contact admin.', 'error');
    await logout();
    return;
  }

  const isUsernameUser = user.email && user.email.endsWith(TENANT_LOCAL_SUFFIX);

  // ensureUserDoc writes the Google-user mirror; skip for username users
  // (their /users/{uid} is created server-side by createStaffLogin).
  if (!isUsernameUser) {
    try { await ensureUserDoc(user); }
    catch (err) { console.warn('[ensureUserDoc]', err); }
  }

  try {
    _tenantConfig = await loadTenantConfig(_currentTenant);
    if (!_tenantConfig) {
      console.warn(`[boot] tenant config doc missing for ${_currentTenant} — running with bootstrap defaults`);
    }
  } catch (err) {
    console.warn('[loadTenantConfig]', err);
  }

  // R5 — apply tenant theme BEFORE renderApp so first paint is themed.
  // Falsy/missing fields fall back to CSS defaults (CCY beige-coffee).
  // Synchronous; no FOUC.
  applyTenantTheme(_tenantConfig?.theme || null);

  // ── R2: load manifest-ized modules from the tenant's enabled list ──
  // Today only `inventory` is registered; every other module remains
  // hardcoded in _routes above. registry.loadEnabledModules() silently
  // skips ids it doesn't have a manifest for (R2 transitional state),
  // so passing the full enabledModules list is safe.
  // If tenantConfig is missing (bootstrap path), default to enabling
  // every module currently registered — keeps Inventory visible even
  // before the /tenants/{tid} doc is seeded.
  const enabledModules = (_tenantConfig && Array.isArray(_tenantConfig.enabledModules))
    ? _tenantConfig.enabledModules
    : ['dashboard', 'inventory', 'suppliers', 'procurement', 'sales',
       'finance', 'hr', 'leave', 'payroll', 'account',
       'policies',   // 2026-09-21 — Staff Policies (read-only, every role incl. shop-floor staff)
       'roadmap',    // 2026-08-15 — Roadmap & Strategy (director/admin only)
       'settings'];  // R3 + finance (2026-05-20)
  // NOTE (2026-09-21): this list's ORDER no longer decides sidebar order —
  // route.sidebarPosition does (see renderApp). Order here is load order only.
  // A2 (2026-04-25) — `invoicing` is intentionally NOT in the bootstrap
  // default. CCY's manufacturing pack ships `sales` which supersedes
  // invoicing; loading invoicing on the bootstrap path would just run
  // the supersededBy filter to drop its routes anyway. New Services
  // tenants get `invoicing` from their tenantConfig (set by onboard-tenant).
  try {
    await registry.loadEnabledModules(enabledModules, {
      user, tenantId: _currentTenant
    });
    // Merge dynamic routes into the static _routes map.
    // Manifests are the source of truth — they replace any hardcoded
    // entry with the same key (defensive: if R3 forgets to remove the
    // hardcoded version, the manifest still wins).
    const dynamicRoutes = registry.getRoutesFor(enabledModules);
    for (const [routeId, route] of Object.entries(dynamicRoutes)) {
      _routes[routeId] = route;
    }
    // Dashboard special-case: wrap its manifest-supplied mount with the
    // onJumpToX callbacks main.js owns (navigate is local to this file).
    // Widgets call ctx.onJump('inventory') etc.; the dashboard host
    // builds those from these closures.
    if (_routes.dashboard) {
      const baseDashboardMount = _routes.dashboard.mount;
      _routes.dashboard = {
        ..._routes.dashboard,
        mount: (container, ctx) => baseDashboardMount(container, {
          ...ctx,
          onJumpToInventory: () => navigate('inventory'),
          onJumpToPOs:       () => navigate('purchase-orders'),
          onJumpToGRNs:      () => navigate('grn'),
          onJumpToInvoices:  () => navigate('supplier-invoices'),
          onJumpToLeave:     () => navigate('leave-management'),
          onJumpToEmployees: () => navigate('employees'),
          // Access-lifecycle (2026-07-15) — the two keep-surfaces a stopped
          // staff can still reach, wired for the stopped-dashboard buttons.
          onJumpToPayslips:  () => navigate('my-payslips'),
          onJumpToProfile:   () => navigate('my-profile'),
          // 2026-07-27 — GENERIC router handle. The onJumpToX closures above
          // are a hardcoded legacy list; a widget for a route missing from it
          // used to silently do nothing when clicked. dashboard.js's _onJump
          // now falls back to this, so any module's widget can navigate to any
          // route without a new closure being added here first.
          onNavigate:        (routeKey) => navigate(routeKey)
        })
      };
    }
  } catch (err) {
    // Hard-fail policy: if a manifest module can't load, refuse to boot
    // rather than silently dropping a sidebar item. (No-silent-deferrals.)
    console.error('[boot] registry.loadEnabledModules failed:', err);
    showToast('Failed to load app modules — please refresh. Details in console.', 'error');
    throw err;
  }

  _currentRole = await getUserRole(user);

  // 2026-05-04 sidebar-leak fix — load the materialized roles map for the
  // current tenant so the sidebar's permission-gated visibility checks
  // (see manifests' visible(role, hasPerm)) consult literal permission
  // strings, not just role-name string-matching. If the read fails (e.g.
  // pre-R6 tenant where the subcollection wasn't materialized), fall back
  // to an empty map — director still works via the fast-path; non-system
  // roles will see ONLY routes whose `visible` accepts a missing perm doc
  // (we made these explicit per-manifest below).
  try {
    _rolesMap = await loadRolesForTenant(_currentTenant);
  } catch (err) {
    console.warn('[boot] loadRolesForTenant failed — sidebar will show only system-role-visible routes.', err);
    _rolesMap = {};
  }

  // Access-lifecycle (2026-07-15) + Phase 5C force-pw gate.
  // Read the /users/{uid} doc ONCE for EVERY user (previously only
  // username users read it, for the force-pw check). The same doc drives:
  //   - _isStopped               (ALL users — offboarding overlay)
  //   - forcePasswordChange gate (username users only)
  let _userDoc = null;
  try {
    _userDoc = await fetchUserDoc(user.uid);
  } catch (err) {
    // Non-fatal — a failed read leaves _isStopped false (fail-OPEN on the
    // client is acceptable: firestore.rules isStopped() is the real gate,
    // and it fails CLOSED. The client overlay is UX, not enforcement).
    console.warn('[boot] user doc read failed:', err);
  }
  _isStopped = isAccessStopped(_userDoc);

  // Phase 5C — force-password-change gate for username users.
  if (!skipForceCheck && isUsernameUser) {
    if (_userDoc && _userDoc.forcePasswordChange === true) {
      renderForcePasswordChangeScreen(user);
      return;
    }
  }

  navigate('dashboard');
}

// R5 — listen for live theme updates from the Branding card. The card
// already updates _tenantConfig.theme in-memory and applies CSS vars;
// we only need to re-render the sidebar so the brand-name / logo /
// tagline pick up changes immediately.
window.addEventListener('tenant-theme-updated', (e) => {
  if (_currentUser) renderApp();
  // applyTenantTheme already called by the card; document.title
  // already updated. Nothing else to do.
  void e;
});

// A5 (2026-04-25) — if a reset link was opened in a browser that's already
// signed in, log out first so the reset-screen flow runs cleanly. The token
// completion will sign them in fresh anyway.
function _hasResetParam() {
  try {
    return new URLSearchParams(window.location.search).has('reset');
  } catch (e) { return false; }
}

onAuth(async (user) => {
  if (user && _hasResetParam()) {
    // Reset flow takes priority — log the existing session out so the
    // reset screen renders. (Token completion writes a fresh password
    // and the user signs in again with new creds.)
    try { await logout(); } catch (e) {}
    return;
  }
  if (!user) {
    _currentUser   = null;
    _currentRole   = null;
    _currentTenant = null;
    _tenantConfig  = null;
    _rolesMap      = null;   // 2026-05-04 sidebar-leak fix — clear stale perms on logout
    _isStopped     = false;  // access-lifecycle — clear stopped overlay on logout
    // R5 — clear post-auth tenant theme on logout. A3: subdomain pre-auth
    // branding STAYS (it's a property of the URL, not the user) — the
    // login card on `acme.erp...` should still show ACME's brand after
    // logout. resetTenantTheme() clears CSS vars; subsequent
    // bootstrapLoginDOM() call re-applies subdomain primary/accent colors
    // from the cached _resolvedBranding.
    resetTenantTheme();
    if (_resolvedBranding?.brandName) {
      document.title = _resolvedBranding.brandName + ' · ERP';
    } else {
      document.title = 'CCY ERP · V2';
    }
    // Re-bootstrap the login screen in case we'd replaced it with the
    // force-password-change or reset screen previously.
    bootstrapLoginDOM();
    renderLoginScreen();
    return;
  }
  if (user.email && !_isAllowedLoginEmail(user.email)) {
    await logout();
    showToast('Only @ccyengineering.com or staff-login accounts are allowed', 'error');
    return;
  }
  await _bootAfterAuth(user);
});
